import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Plus, Search, Pencil, Trash2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const CATEGORIES = ["Bedding", "Curtain", "Table Linen", "Placemat", "Throw", "Other"];

const EMPTY = { sku: "", productName: "", category: "Bedding", size: "", material: "", pieces: "", upc: "", description: "", careInstructions: "", status: "Active" };

function ProductForm({ value, onChange, mainImage, onMainImage, uploading }) {
  const { t } = useTranslation();
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <div className="sm:col-span-2 flex items-center gap-4">
        <div className="h-20 w-20 shrink-0 overflow-hidden rounded-lg border border-slate-200 bg-slate-50">
          {mainImage ? <img src={fileUrl(mainImage)} alt="" className="h-full w-full object-cover" /> : null}
        </div>
        <div>
          <Label className="text-xs">{t("products.mainImage")}</Label>
          <Input type="file" accept="image/*" onChange={onMainImage} disabled={uploading} className="mt-1" data-testid="product-main-image-input" />
        </div>
      </div>
      <div><Label>{t("products.sku")}</Label><Input className="mt-1" value={value.sku} onChange={(e) => onChange({ ...value, sku: e.target.value })} data-testid="product-sku-input" /></div>
      <div><Label>{t("products.name")}</Label><Input className="mt-1" value={value.productName} onChange={(e) => onChange({ ...value, productName: e.target.value })} data-testid="product-name-input" /></div>
      <div>
        <Label>{t("products.category")}</Label>
        <Select value={value.category} onValueChange={(v) => onChange({ ...value, category: v })}>
          <SelectTrigger className="mt-1" data-testid="product-category-select"><SelectValue /></SelectTrigger>
          <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div>
        <Label>{t("products.status")}</Label>
        <Select value={value.status} onValueChange={(v) => onChange({ ...value, status: v })}>
          <SelectTrigger className="mt-1" data-testid="product-status-select"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="Active">{t("products.active")}</SelectItem><SelectItem value="Inactive">{t("products.inactive")}</SelectItem></SelectContent>
        </Select>
      </div>
      <div><Label>{t("products.size")}</Label><Input className="mt-1" value={value.size} onChange={(e) => onChange({ ...value, size: e.target.value })} data-testid="product-size-input" /></div>
      <div><Label>{t("products.material")}</Label><Input className="mt-1" value={value.material} onChange={(e) => onChange({ ...value, material: e.target.value })} data-testid="product-material-input" /></div>
      <div><Label>{t("products.pieces")}</Label><Input type="number" className="mt-1" value={value.pieces} onChange={(e) => onChange({ ...value, pieces: e.target.value })} data-testid="product-pieces-input" /></div>
      <div><Label>{t("products.upc")}</Label><Input className="mt-1" value={value.upc} onChange={(e) => onChange({ ...value, upc: e.target.value })} data-testid="product-upc-input" /></div>
      <div className="sm:col-span-2"><Label>{t("products.description")}</Label><Textarea className="mt-1" rows={3} value={value.description} onChange={(e) => onChange({ ...value, description: e.target.value })} data-testid="product-description-input" /></div>
      <div className="sm:col-span-2"><Label>{t("products.care")}</Label><Textarea className="mt-1" rows={2} value={value.careInstructions} onChange={(e) => onChange({ ...value, careInstructions: e.target.value })} data-testid="product-care-input" /></div>
    </div>
  );
}

export default function Products() {
  const { t } = useTranslation();
  const { isAdmin } = useAuth();
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [mainImage, setMainImage] = useState(null);
  const [uploading, setUploading] = useState(false);

  const load = async (q = "") => {
    const { data } = await api.get("/products", { params: { search: q } });
    setProducts(data);
  };

  useEffect(() => { load(); }, []);
  useEffect(() => {
    const tm = setTimeout(() => load(search), 300);
    return () => clearTimeout(tm);
  }, [search]);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setMainImage(null); setOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm({ ...EMPTY, ...p, pieces: p.pieces ?? "" }); setMainImage(p.mainImage); setOpen(true); };

  const handleMainImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setMainImage(data);
    } catch (err) {
      toast.error(t("products.imageUploadFailed"));
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    try {
      const payload = { ...form, pieces: form.pieces ? parseInt(form.pieces) : null, mainImage };
      if (editing) {
        await api.put(`/products/${editing.id}`, payload);
        toast.success(t("products.updated"));
      } else {
        await api.post("/products", payload);
        toast.success(t("products.created"));
      }
      setOpen(false);
      load(search);
    } catch (err) {
      toast.error(t("products.saveFailed"));
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/products/${id}`);
      toast.success(t("products.deleted"));
      load(search);
    } catch (err) {
      toast.error(t("products.deleteFailed"));
    }
  };

  return (
    <div className="mx-auto max-w-6xl px-8 py-10" data-testid="products-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-display text-2xl font-700 text-slate-900">{t("products.title")}</h1>
        {isAdmin && (
          <Button className="bhf-btn-primary gap-1.5 border-0 text-white" onClick={openCreate} data-testid="add-product-button">
            <Plus className="h-4 w-4" /> {t("products.add")}
          </Button>
        )}
      </div>
      <div className="mt-5 relative max-w-sm">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <Input className="pl-9" placeholder={t("products.searchPlaceholder")} value={search} onChange={(e) => setSearch(e.target.value)} data-testid="product-search-input" />
      </div>

      <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
        {products.map((p) => (
          <div key={p.id} className="bhf-card overflow-hidden" data-testid={`product-card-${p.id}`}>
            <div className="aspect-square w-full bg-slate-100">
              {p.mainImage?.url ? <img src={fileUrl(p.mainImage)} alt={p.productName} className="h-full w-full object-cover" /> : <div className="flex h-full items-center justify-center text-xs text-slate-400">{t("products.noImage")}</div>}
            </div>
            <div className="p-3.5">
              <div className="flex items-center justify-between">
                <span className="font-mono-bhf text-[11px] text-indigo-600">{p.sku}</span>
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${p.status === "Active" ? "bg-emerald-50 text-emerald-600" : "bg-slate-100 text-slate-500"}`}>{p.status === "Active" ? t("products.active") : t("products.inactive")}</span>
              </div>
              <div className="mt-1 truncate text-sm font-semibold text-slate-800">{p.productName}</div>
              <div className="mt-0.5 text-xs text-slate-500">{p.category} · {p.size}</div>
              {isAdmin && (
              <div className="mt-3 flex gap-2">
                <Button size="sm" variant="outline" className="flex-1" onClick={() => openEdit(p)} data-testid={`edit-product-${p.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button size="sm" variant="outline" className="flex-1 text-red-500 hover:text-red-600" data-testid={`delete-product-${p.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>{t("products.deleteTitle")}</AlertDialogTitle>
                      <AlertDialogDescription>{t("products.deleteDesc", { name: p.productName })}</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
                      <AlertDialogAction onClick={() => handleDelete(p.id)} data-testid={`confirm-delete-product-${p.id}`}>{t("common.delete")}</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
              )}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="product-dialog">
          <DialogHeader><DialogTitle>{editing ? t("products.editTitle") : t("products.addTitle")}</DialogTitle></DialogHeader>
          <ProductForm value={form} onChange={setForm} mainImage={mainImage} onMainImage={handleMainImage} uploading={uploading} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>{t("common.cancel")}</Button>
            <Button className="bhf-btn-primary border-0 text-white" onClick={handleSave} data-testid="save-product-button">{t("common.save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
